import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { masterService } from '../../services/masterService';
import { ObservationPickerDrawer } from './drawers/ObservationPickerDrawer';
import { CreateObservationDrawer } from './drawers/CreateObservationDrawer';
import { ObservationConfigDrawer } from './drawers/ObservationConfigDrawer';
import './InvestigationObservationMappingPage.css';

export const InvestigationObservationMappingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Investigation context selection
  const [investigationsList, setInvestigationsList] = useState([]);
  const [selectedInvId, setSelectedInvId] = useState(null);
  const [activeInvestigation, setActiveInvestigation] = useState(null);
  const [loadingInvList, setLoadingInvList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Mapped observations working state
  const [observations, setObservations] = useState([]);
  const [initialObservationsJson, setInitialObservationsJson] = useState('[]');
  const [saving, setSaving] = useState(false);

  // Filter within mapped parameters
  const [filterTerm, setFilterTerm] = useState('');

  // Drawers & Modals
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [confirmRemoveObs, setConfirmRemoveObs] = useState(null);

  // Drag and Drop state
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Toast feedback
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  }, []);

  // 1. Fetch initial investigations list
  useEffect(() => {
    setLoadingInvList(true);
    masterService.getInvestigations({ pageSize: 500, isActive: true })
      .then(res => {
        const items = res?.data?.items || res?.data || [];
        setInvestigationsList(items);

        // Preselect from URL query param or fallback to first investigation
        const urlId = searchParams.get('invId');
        if (urlId) {
          const parsed = parseInt(urlId, 10);
          if (!isNaN(parsed)) {
            setSelectedInvId(parsed);
            return;
          }
        }
        if (items.length > 0) {
          setSelectedInvId(items[0].investigationId);
        }
      })
      .catch(err => {
        console.error('Failed to load investigations list', err);
        showToast('Failed to load investigations list', 'error');
      })
      .finally(() => {
        setLoadingInvList(false);
      });
  }, [searchParams, showToast]);

  // 2. Fetch full detail and mapped observations whenever selectedInvId changes
  const loadInvestigationDetail = useCallback((invId) => {
    if (!invId) return;
    setLoadingDetail(true);

    masterService.getInvestigationById(invId)
      .then(res => {
        const detail = res?.data;
        if (detail) {
          setActiveInvestigation(detail);
          const mapped = (detail.observations || []).map((o, idx) => ({
            ...o,
            printOrder: o.printOrder || idx + 1,
            prefix: o.prefix || '',
            childFlag: !!o.childFlag,
            isCritical: !!o.isCritical,
            isAmr: !!o.isAmr,
            isReflex: !!o.isReflex,
            isComment: !!o.isComment,
            isBold: !!o.isBold,
            isUnderLine: !!o.isUnderLine,
            sepratePrint: !!o.sepratePrint,
            chkHelp: !!o.chkHelp,
            isAutoConsume: !!o.isAutoConsume,
            microscopy: !!o.microscopy,
            showNabl: o.showNabl !== undefined ? !!o.showNabl : true,
          }));

          setObservations(mapped);
          setInitialObservationsJson(JSON.stringify(mapped));
        }
      })
      .catch(err => {
        console.error('Failed to load investigation detail', err);
        showToast('Failed to load mapped parameters for selected investigation', 'error');
      })
      .finally(() => {
        setLoadingDetail(false);
      });
  }, [showToast]);

  useEffect(() => {
    if (selectedInvId) {
      loadInvestigationDetail(selectedInvId);
      // Sync URL
      setSearchParams({ invId: selectedInvId });
    }
  }, [selectedInvId, loadInvestigationDetail, setSearchParams]);

  // Dirty tracking
  const isDirty = useMemo(() => {
    return JSON.stringify(observations) !== initialObservationsJson;
  }, [observations, initialObservationsJson]);

  // Filtered mapped observations
  const filteredObservations = useMemo(() => {
    if (!filterTerm.trim()) return observations;
    const term = filterTerm.trim().toLowerCase();
    return observations.filter(o => {
      const name = (o.observationName || '').toLowerCase();
      const code = (o.labObservationId || '').toLowerCase();
      const unit = (o.unit || '').toLowerCase();
      const prefix = (o.prefix || '').toLowerCase();
      return name.includes(term) || code.includes(term) || unit.includes(term) || prefix.includes(term);
    });
  }, [observations, filterTerm]);

  // Combined investigations list ensuring currently active investigation is always included in dropdown options
  const displayInvestigationsList = useMemo(() => {
    if (!activeInvestigation) return investigationsList;
    const activeId = activeInvestigation.investigationId;
    const exists = investigationsList.some(
      inv => (inv.investigationId || inv.id) === activeId
    );
    if (!exists) {
      return [
        {
          investigationId: activeId,
          name: activeInvestigation.name || activeInvestigation.investigationName,
          testCode: activeInvestigation.testCode,
          departmentName: activeInvestigation.departmentName,
        },
        ...investigationsList,
      ];
    }
    return investigationsList;
  }, [investigationsList, activeInvestigation]);

  // Already mapped observation IDs
  const alreadyMappedIds = useMemo(() => {
    return observations.map(o => o.labObservationId);
  }, [observations]);

  // Handle re-ordering
  const handleMove = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= observations.length) return;

    setObservations(prev => {
      const copy = [...prev];
      const temp = copy[index];
      copy[index] = copy[target];
      copy[target] = temp;
      // Re-index printOrder
      return copy.map((item, idx) => ({ ...item, printOrder: idx + 1 }));
    });
  };

  // Drag and Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      return;
    }

    setObservations(prev => {
      const copy = [...prev];
      const [draggedItem] = copy.splice(draggedIndex, 1);
      copy.splice(targetIndex, 0, draggedItem);
      return copy.map((item, idx) => ({ ...item, printOrder: idx + 1 }));
    });
    setDraggedIndex(null);
  };

  // Add existing observations from drawer
  const handleAddSelectedFromPicker = (selectedList) => {
    const newMappings = selectedList.map((obs, idx) => ({
      investigationId: selectedInvId,
      labObservationId: obs.labObservationId,
      observationName: obs.name,
      readingFormat: obs.readingFormat || 'Numeric',
      unit: obs.unit || '',
      printOrder: observations.length + idx + 1,
      prefix: '',
      childFlag: false,
      isCritical: !!(obs.minCritical || obs.maxCritical),
      isamr: false,
      isreflex: false,
      isComment: !!obs.isComment,
      isBold: false,
      isUnderLine: false,
      sepratePrint: false,
      chkHelp: false,
      isAutoConsume: false,
      microscopy: false,
      parentId: null,
      showNabl: true,
    }));

    setObservations(prev => [...prev, ...newMappings]);
    showToast(`Added ${newMappings.length} parameter${newMappings.length > 1 ? 's' : ''} to mapping`);
  };

  // Add newly created observation in-place
  const handleCreatedAndMap = (newMapping) => {
    const itemWithOrder = {
      ...newMapping,
      investigationId: selectedInvId,
      printOrder: observations.length + 1,
    };
    setObservations(prev => [...prev, itemWithOrder]);
    showToast(`Parameter "${newMapping.observationName}" created and added to mapping!`);
  };

  // Update mapping configuration from drawer
  const handleSaveConfig = (updatedConfig) => {
    setObservations(prev =>
      prev.map(item =>
        item.labObservationId === updatedConfig.labObservationId ? updatedConfig : item
      )
    );
    showToast(`Configuration updated for "${updatedConfig.observationName}"`);
  };

  // Direct row flag toggler for Header (childFlag), Bold (isBold), UnderLine (isUnderLine)
  const handleToggleRowFlag = (idx, flagKey) => {
    setObservations(prev => {
      const next = [...prev];
      const currentVal = !next[idx][flagKey];
      const updated = {
        ...next[idx],
        [flagKey]: currentVal
      };
      // When marked as Header, automatically make it bold by default
      if (flagKey === 'childFlag' && currentVal) {
        updated.isBold = true;
      }
      next[idx] = updated;
      return next;
    });
  };

  // Remove observation
  const handleConfirmRemove = () => {
    if (!confirmRemoveObs) return;
    const targetId = confirmRemoveObs.labObservationId;
    setObservations(prev => {
      const filtered = prev.filter(o => o.labObservationId !== targetId);
      return filtered.map((item, idx) => ({ ...item, printOrder: idx + 1 }));
    });
    showToast(`Unmapped "${confirmRemoveObs.observationName}" from ${activeInvestigation?.name || 'investigation'}`);
    setConfirmRemoveObs(null);
  };

  // Discard changes
  const handleDiscardChanges = () => {
    setObservations(JSON.parse(initialObservationsJson));
    showToast('Changes discarded', 'info');
  };

  // Save complete mapping to backend
  const handleSaveAll = async () => {
    if (!selectedInvId) return;
    setSaving(true);

    try {
      // Clean payload matching backend InvestigationObservationMappingDto
      const payload = observations.map((obs, idx) => ({
        investigationId: selectedInvId,
        labObservationId: obs.labObservationId,
        observationName: obs.observationName,
        readingFormat: obs.readingFormat || 'Numeric',
        unit: obs.unit || null,
        printOrder: idx + 1,
        prefix: obs.prefix || '',
        childFlag: !!obs.childFlag,
        isCritical: !!obs.isCritical,
        isAmr: !!obs.isAmr,
        isReflex: !!obs.isReflex,
        isComment: !!obs.isComment,
        isBold: !!obs.isBold,
        isUnderLine: !!obs.isUnderLine,
        sepratePrint: !!obs.sepratePrint,
        chkHelp: !!obs.chkHelp,
        isAutoConsume: !!obs.isAutoConsume,
        microscopy: !!obs.microscopy,
        parentId: obs.parentId || null,
        isActive: true,
      }));

      await masterService.updateInvestigationObservations(selectedInvId, payload);
      showToast('Investigation parameter mapping saved successfully!', 'success');

      // Reload fresh state from server to guarantee verification
      await loadInvestigationDetail(selectedInvId);
    } catch (err) {
      console.error('Failed to save mapping', err);
      const msg = err.response?.data?.message || err.message || 'Failed to save mapped parameters';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="iom-workspace-container">
      {/* Toast Notification Container */}
      <div className="iom-toasts-container">
        {toasts.map(t => (
          <div key={t.id} className={`iom-toast iom-toast-${t.type}`}>
            <span className="iom-toast-icon">
              {t.type === 'success' && '✓'}
              {t.type === 'error' && '✕'}
              {t.type === 'info' && 'ℹ'}
            </span>
            <span className="iom-toast-text">{t.message}</span>
          </div>
        ))}
      </div>

      {/* ── TOP CONTEXT HEADER ── */}
      <header className="iom-context-header">
        <div className="iom-context-top-row">
          <div className="iom-breadcrumb-area">
            <span className="iom-breadcrumb-root" onClick={() => navigate('/masters?tab=investigations')}>
              Investigation Master
            </span>
            <span className="iom-breadcrumb-sep">/</span>
            <span className="iom-breadcrumb-current">Observation & Parameter Mapping</span>
          </div>

          {/* Quick Investigation Selector Dropdown */}
          <div className="iom-investigation-select-wrapper">
            <label htmlFor="iom-select-inv" className="iom-inv-select-label">
              Active Investigation:
            </label>
            <select
              id="iom-select-inv"
              className="iom-select-investigation"
              value={selectedInvId || activeInvestigation?.investigationId || ''}
              onChange={e => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val)) {
                  setSelectedInvId(val);
                }
              }}
              disabled={loadingInvList || saving}
            >
              {displayInvestigationsList.map(inv => (
                <option key={inv.investigationId || inv.id} value={inv.investigationId || inv.id}>
                  {inv.name || inv.investigationName} ({inv.testCode}) — {inv.departmentName || 'General'}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Active Investigation Context Summary Card */}
        {activeInvestigation && (
          <div className="iom-active-inv-card">
            <div className="iom-active-inv-main">
              <div className="iom-inv-badge-row">
                <span className="iom-inv-code-chip">{activeInvestigation.testCode || 'NO-CODE'}</span>
                <span className="iom-inv-dept-chip">{activeInvestigation.departmentName || 'Diagnostic Laboratory'}</span>
                <span className="iom-inv-type-chip">
                  {activeInvestigation.reportType === 1 ? 'Pathology / Numeric' : 'Pathology Report'}
                </span>
                <span className="iom-inv-count-chip">
                  {observations.length} Parameter{observations.length !== 1 ? 's' : ''} Configured
                </span>
              </div>
              <h2 className="iom-active-inv-title">{activeInvestigation.name}</h2>
              {activeInvestigation.sampleRemarks && (
                <p className="iom-active-inv-desc">{activeInvestigation.sampleRemarks}</p>
              )}
            </div>

            <div className="iom-active-inv-actions">
              <button
                type="button"
                className="iom-btn-inv-action"
                onClick={() => navigate(`/investigations?id=${activeInvestigation.investigationId}`)}
                title="Edit parent investigation general settings in Master catalog"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                <span>Edit Investigation Master</span>
              </button>
            </div>
          </div>
        )}
      </header>

      {/* ── WORKSPACE TOOLBAR ── */}
      <div className="iom-workspace-toolbar">
        <div className="iom-toolbar-left">
          <div className="iom-search-filter-box">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              className="iom-filter-input"
              placeholder="Filter mapped parameters..."
              value={filterTerm}
              onChange={e => setFilterTerm(e.target.value)}
            />
            {filterTerm && (
              <button
                type="button"
                className="iom-filter-clear-btn"
                onClick={() => setFilterTerm('')}
              >
                ✕
              </button>
            )}
          </div>
          <span className="iom-toolbar-metric">
            Showing <strong>{filteredObservations.length}</strong> of {observations.length}
          </span>
        </div>

        <div className="iom-toolbar-right">
          <button
            type="button"
            className="iom-btn iom-btn-secondary"
            onClick={() => setIsCreateOpen(true)}
            disabled={!selectedInvId}
          >
            <span className="iom-btn-plus">+</span>
            <span>Create New Parameter</span>
          </button>
          <button
            type="button"
            className="iom-btn iom-btn-primary"
            onClick={() => setIsPickerOpen(true)}
            disabled={!selectedInvId}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
            <span>Add Existing Parameters</span>
          </button>
        </div>
      </div>

      {/* ── MAPPED OBSERVATIONS DATA GRID ── */}
      <main className="iom-grid-card">
        {loadingDetail ? (
          <div className="iom-loading-workspace">
            <div className="iom-spinner" />
            <span>Loading investigation mapping profile...</span>
          </div>
        ) : observations.length === 0 ? (
          <div className="iom-empty-workspace">
            <div className="iom-empty-illustration">🧪</div>
            <h3 className="iom-empty-title">No Parameters Mapped Yet</h3>
            <p className="iom-empty-sub">
              This investigation currently does not have any clinical observations or analytes configured.
              Add existing parameters from the laboratory catalog or define brand-new analytes on-the-fly.
            </p>
            <div className="iom-empty-buttons">
              <button
                type="button"
                className="iom-btn iom-btn-primary"
                onClick={() => setIsPickerOpen(true)}
              >
                + Add Existing Parameters
              </button>
              <button
                type="button"
                className="iom-btn iom-btn-secondary"
                onClick={() => setIsCreateOpen(true)}
              >
                + Create New Parameter
              </button>
            </div>
          </div>
        ) : (
          <div className="iom-table-container">
            <table className="iom-data-table">
              <thead>
                <tr>
                  <th style={{ width: '80px', textAlign: 'center' }}>Order</th>
                  <th style={{ width: '270px' }}>Parameter / Analyte</th>
                  <th style={{ width: '120px' }}>Type & Unit</th>
                  <th style={{ width: '75px', textAlign: 'center' }}>Header</th>
                  <th style={{ width: '70px', textAlign: 'center' }}>Bold</th>
                  <th style={{ width: '85px', textAlign: 'center' }}>UnderLine</th>
                  <th style={{ width: '160px' }}>Compliance & Flags</th>
                  <th style={{ width: '105px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredObservations.map((obs, idx) => {
                  const isHeader = !!obs.childFlag;
                  const isDragging = draggedIndex === idx;

                  return (
                    <tr
                      key={obs.labObservationId || idx}
                      draggable={!filterTerm}
                      onDragStart={e => handleDragStart(e, idx)}
                      onDragOver={e => handleDragOver(e, idx)}
                      onDrop={e => handleDrop(e, idx)}
                      className={`iom-table-row ${isHeader ? 'iom-row-header-divider' : ''} ${isDragging ? 'iom-row-dragging' : ''}`}
                    >
                      {/* Column 1: Order / Reorder controls */}
                      <td style={{ textAlign: 'center' }}>
                        <div className="iom-order-cell">
                          <span
                            className="iom-drag-handle"
                            title="Drag to reorder sequence"
                          >
                            ⋮⋮
                          </span>
                          <span className="iom-order-badge">#{obs.printOrder || idx + 1}</span>
                          <div className="iom-nudge-buttons">
                            <button
                              type="button"
                              className="iom-btn-nudge"
                              disabled={idx === 0}
                              onClick={() => handleMove(idx, -1)}
                              title="Move parameter up"
                            >
                              ▲
                            </button>
                            <button
                              type="button"
                              className="iom-btn-nudge"
                              disabled={idx === observations.length - 1}
                              onClick={() => handleMove(idx, 1)}
                              title="Move parameter down"
                            >
                              ▼
                            </button>
                          </div>
                        </div>
                      </td>

                      {/* Column 2: Parameter Identity */}
                      <td>
                        <div className="iom-param-info">
                          <div className="iom-param-title-line">
                            {obs.prefix && <span className="iom-tag-prefix">{obs.prefix}</span>}
                            <span
                              className={`iom-param-name ${obs.isBold ? 'text-bold' : ''}`}
                              style={{ textDecoration: obs.isUnderLine ? 'underline' : 'none' }}
                            >
                              {obs.observationName || obs.labObservationId}
                            </span>
                          </div>
                          <div className="iom-param-code-line">
                            <span className="iom-param-code">{obs.labObservationId}</span>
                            {isHeader && (
                              <span className="iom-badge-pill iom-pill-header">Header Row</span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Column 3: Type & Unit */}
                      <td>
                        <div className="iom-type-unit-cell">
                          <span className="iom-type-badge">{obs.readingFormat || 'Numeric'}</span>
                          {obs.unit ? (
                            <span className="iom-unit-text">{obs.unit}</span>
                          ) : (
                            <span className="iom-unit-empty">—</span>
                          )}
                        </div>
                      </td>

                      {/* Column 4: Header Checkbox */}
                      <td style={{ textAlign: 'center' }}>
                        <label className="iom-table-chk-label" title="Category / Group Header row (no direct result entry)">
                          <input
                            type="checkbox"
                            className="iom-table-chk"
                            checked={!!obs.childFlag}
                            onChange={() => handleToggleRowFlag(idx, 'childFlag')}
                          />
                        </label>
                      </td>

                      {/* Column 5: Bold Checkbox */}
                      <td style={{ textAlign: 'center' }}>
                        <label className="iom-table-chk-label" title="Print parameter name in bold on report">
                          <input
                            type="checkbox"
                            className="iom-table-chk"
                            checked={!!obs.isBold}
                            onChange={() => handleToggleRowFlag(idx, 'isBold')}
                          />
                        </label>
                      </td>

                      {/* Column 6: UnderLine Checkbox */}
                      <td style={{ textAlign: 'center' }}>
                        <label className="iom-table-chk-label" title="Print parameter name with underline on report">
                          <input
                            type="checkbox"
                            className="iom-table-chk"
                            checked={!!obs.isUnderLine}
                            onChange={() => handleToggleRowFlag(idx, 'isUnderLine')}
                          />
                        </label>
                      </td>

                      {/* Column 7: Compliance & Other Flags */}
                      <td>
                        <div className="iom-flags-group">
                          {obs.showNabl ? (
                            <span className="iom-flag-active iom-flag-nabl" title="ISO 15189 NABL accreditation marker active">
                              ✓ NABL
                            </span>
                          ) : (
                            <span className="iom-flag-inactive">NABL</span>
                          )}
                          {obs.isCritical && (
                            <span className="iom-flag-active iom-flag-critical" title="Panic / Critical value alerting">
                              Critical
                            </span>
                          )}
                          {obs.sepratePrint ? (
                            <span className="iom-flag-active iom-flag-purple" title="Prints on separate page/section">
                              Sep. Page
                            </span>
                          ) : null}
                          {obs.isComment && (
                            <span className="iom-flag-active iom-flag-comment" title="Comment / Narrative block">
                              Comment
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Column 7: Actions */}
                      <td style={{ textAlign: 'center' }}>
                        <div className="iom-actions-cell">
                          <button
                            type="button"
                            className="iom-btn-row-action iom-btn-config"
                            onClick={() => setEditingMapping(obs)}
                            title="Configure display, report typography, and clinical rules"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="3"/>
                              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                            </svg>
                            <span>Config</span>
                          </button>
                          <button
                            type="button"
                            className="iom-btn-row-action iom-btn-remove"
                            onClick={() => setConfirmRemoveObs(obs)}
                            title="Remove parameter from this investigation"
                          >
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* ── STICKY SAVE BAR ── */}
      <footer className={`iom-sticky-savebar ${isDirty ? 'visible' : ''}`}>
        <div className="iom-savebar-content">
          <div className="iom-savebar-status">
            <span className="iom-savebar-dot" />
            <div>
              <strong>Unsaved Configuration Changes</strong>
              <span className="iom-savebar-hint">
                {observations.length} parameter{observations.length !== 1 ? 's' : ''} in sequence
              </span>
            </div>
          </div>

          <div className="iom-savebar-actions">
            <button
              id="discard-mapping-changes-btn"
              type="button"
              className="iom-btn iom-btn-secondary"
              onClick={handleDiscardChanges}
              disabled={saving}
            >
              Discard Changes
            </button>
            <button
              id="save-complete-mapping-btn"
              type="button"
              className="iom-btn iom-btn-save"
              onClick={handleSaveAll}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="iom-spinner-inline" />
                  <span>Saving Configuration...</span>
                </>
              ) : (
                <span>Save Complete Mapping</span>
              )}
            </button>
          </div>
        </div>
      </footer>

      {/* ── DRAWERS & MODALS ── */}

      {/* 1. Add Existing Observations Picker Drawer */}
      <ObservationPickerDrawer
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        alreadyMappedIds={alreadyMappedIds}
        activeInvestigationName={activeInvestigation?.name}
        onAddSelected={handleAddSelectedFromPicker}
      />

      {/* 2. Create Brand-New Observation Drawer */}
      <CreateObservationDrawer
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        investigationId={selectedInvId}
        investigationName={activeInvestigation?.name}
        onCreatedAndMap={handleCreatedAndMap}
      />

      {/* 3. Deep Parameter Configuration Drawer */}
      <ObservationConfigDrawer
        isOpen={!!editingMapping}
        onClose={() => setEditingMapping(null)}
        mapping={editingMapping}
        investigationName={activeInvestigation?.name}
        onSaveConfig={handleSaveConfig}
      />

      {/* 4. Safe Remove Confirmation Modal */}
      {confirmRemoveObs && (
        <div className="iom-modal-overlay" onClick={() => setConfirmRemoveObs(null)}>
          <div className="iom-modal-box" onClick={e => e.stopPropagation()}>
            <div className="iom-modal-header">
              <div className="iom-modal-warn-icon">⚠️</div>
              <div>
                <h4 className="iom-modal-title">Remove Parameter Mapping?</h4>
                <p className="iom-modal-sub">
                  Are you sure you want to remove <strong>"{confirmRemoveObs.observationName}"</strong> ({confirmRemoveObs.labObservationId}) from <strong>{activeInvestigation?.name}</strong>?
                </p>
              </div>
            </div>

            <div className="iom-modal-note">
              <strong>Notice:</strong> This action only unlinks the parameter from this specific investigation. The underlying analyte definition in the master catalog will remain intact.
            </div>

            <div className="iom-modal-footer">
              <button
                type="button"
                className="iom-btn iom-btn-secondary"
                onClick={() => setConfirmRemoveObs(null)}
              >
                Keep Parameter
              </button>
              <button
                type="button"
                className="iom-btn iom-btn-danger"
                onClick={handleConfirmRemove}
              >
                Remove from Investigation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestigationObservationMappingPage;
